import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const LotSize = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('lot_sizes');
  const [viewItem, setViewItem] = useState(null);
  const [lotSizes, setLotSizes] = useState([]);
  const [procedures, setProcedures] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingLotSize, setEditingLotSize] = useState(null);

  useEffect(() => {
    loadLotSizes();
    loadProcedures();
    loadIndicators();
  }, []);

  const loadLotSizes = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/lot-sizes`);
      setLotSizes(response.data || []);
    } catch (error) {
      console.error('Error loading lot sizes:', error);
      toast.error('Failed to load lot sizes');
    } finally {
      setIsLoading(false);
    }
  };

  const loadProcedures = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/lot-sizing-procedures`);
      setProcedures(response.data || []);
    } catch (error) {
      console.error('Error loading procedures:', error);
    }
  };

  const loadIndicators = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/lot-size-indicators`);
      setIndicators(response.data || []);
    } catch (error) {
      console.error('Error loading indicators:', error);
    }
  };

  const handleOpenDialog = (lotSize = null) => {
    setEditingLotSize(lotSize);
    setIsDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);

    const data = {
      lot_size_id: (formData.get('lot_size_id') || '').toUpperCase(),
      description: formData.get('description') || '',
      lot_sizing_procedure_id: formData.get('lot_sizing_procedure_id') || '',
      lot_size_indicator_id: formData.get('lot_size_indicator_id') || ''
    };

    try {
      if (editingLotSize) {
        await axios.put(`${BACKEND_URL}/api/lot-sizes/${editingLotSize.lot_size_id}`, {
          description: data.description,
          lot_sizing_procedure_id: data.lot_sizing_procedure_id,
          lot_size_indicator_id: data.lot_size_indicator_id
        });
        toast.success('Lot Size updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/lot-sizes`, data);
        toast.success('Lot Size created successfully');
      }
      setIsDialogOpen(false);
      loadLotSizes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save lot size');
    }
  };

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Lot Sizes</h1>
            <p className="text-sm text-gray-500 mt-1">Manage lot sizing procedures and indicators</p>
          </div>
        </div>

        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Lot Sizes</h2>
            {writable && (<Button onClick={() => handleOpenDialog()}>
              <Plus className="w-4 h-4 mr-2" />
              Add Lot Size
            </Button>)}
          </div>

          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Lot Size ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Description</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Lot Sizing Procedure</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Lot Size Indicator</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {lotSizes.length === 0 ? (
                    <tr>
                      <td colSpan={5} className="text-center py-8 text-gray-500">
                        No lot sizes found. Click &quot;Add Lot Size&quot; to create one.
                      </td>
                    </tr>
                  ) : (
                    lotSizes.map((lotSize) => {
                      const procedure = procedures.find(p => p.lot_sizing_procedure_id === lotSize.lot_sizing_procedure_id);
                      const indicator = indicators.find(i => i.lot_size_indicator_id === lotSize.lot_size_indicator_id);
                      return (
                      <tr key={lotSize.lot_size_id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm font-semibold">{lotSize.lot_size_id}</td>
                        <td className="py-3 px-4 text-sm">{lotSize.description}</td>
                        <td className="py-3 px-4 text-sm">{procedure ? `${procedure.lot_sizing_procedure_id} - ${procedure.description}` : lotSize.lot_sizing_procedure_id || '-'}</td>
                        <td className="py-3 px-4 text-sm">{indicator ? `${indicator.lot_size_indicator_id} - ${indicator.description}` : lotSize.lot_size_indicator_id || '-'}</td>
                        <td className="py-3 px-4 text-right">
                          <RecordActions doctype="lot_sizes" onView={() => setViewItem(lotSize)} onEdit={() => handleOpenDialog(lotSize)} />
                        </td>
                      </tr>
                    )})
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Add/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>{editingLotSize ? 'Edit Lot Size' : 'Add Lot Size'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="lot_size_id">Lot Size ID * (2 characters)</Label>
                  <Input
                    id="lot_size_id"
                    name="lot_size_id"
                    defaultValue={editingLotSize?.lot_size_id || ''}
                    placeholder="e.g., EX, HB"
                    maxLength={2}
                    minLength={2}
                    pattern="[A-Za-z0-9]{2}"
                    title="Must be exactly 2 alphanumeric characters"
                    disabled={!!editingLotSize}
                    className="uppercase"
                    required
                    onInput={(e) => {
                      e.target.value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 2);
                    }}
                  />
                  <p className="text-xs text-gray-500 mt-1">Must be exactly 2 alphanumeric characters</p>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Input
                    name="description"
                    defaultValue={editingLotSize?.description || ''}
                    placeholder="e.g., Lot-for-lot order quantity"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Lot Sizing Procedure *</Label>
                  <select
                    name="lot_sizing_procedure_id"
                    defaultValue={editingLotSize?.lot_sizing_procedure_id || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Select Procedure</option>
                    {procedures.map(proc => (
                      <option key={proc.lot_sizing_procedure_id} value={proc.lot_sizing_procedure_id}>
                        {proc.lot_sizing_procedure_id} - {proc.description}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Lot Size Indicator *</Label>
                  <select
                    name="lot_size_indicator_id"
                    defaultValue={editingLotSize?.lot_size_indicator_id || ''}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                    required
                  >
                    <option value="">Select Indicator</option>
                    {indicators.map(ind => (
                      <option key={ind.lot_size_indicator_id} value={ind.lot_size_indicator_id}>
                        {ind.lot_size_indicator_id} - {ind.description}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-2 justify-end pt-4 border-t">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingLotSize ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default LotSize;
